import { FormInput, FormSelect, ISelectOption, ToggleButton, spreadAndtransformDefaultValues } from "@common/components/forms";
import { PageCard } from "@common/components/layout";
import { YesNoConfirmModal } from "@common/components/modals";
import { SuspenseButton } from "@common/components/utility";
import { useApp, useCurrentRoute, useUnsavedChangesWarning } from "@common/hooks";
import { HospitalModel, MacroKeywords, MacroModel } from "@common/models";
import { AuthOperationUINames } from "@common/models/Enums/AuthOperationStrings";
import { MacroTargetType, MacroType } from "@common/models/Enums/MacroType";
import { HospitalService } from "@common/services";
import { useHospitalApp } from "@hospital/hooks";
import classNames from "classnames";
import { find } from "lodash";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

interface IProps {
	macro: MacroModel | undefined;
	macroScope: MacroType;
	// onSubmit: (data: IMacroModel) => void;
	onSubmit: any;
	onCancel: () => void;
	onDelete: (macroId: number) => void;
	fetchHospitals?: () => void;
	hospitals?: Array<HospitalModel>;
	users: ISelectOption[];
	macroTypes: ISelectOption[];
	selectedUserId?: number;
}

export const CommunicationTemplateForm = observer(
	({
		macro,
		macroScope,
		onSubmit,
		onCancel,
		onDelete,
		fetchHospitals,
		hospitals,
		users,
		macroTypes,
		selectedUserId,
	}: IProps) => {
		const { configuration } = useApp();
		const { currentHospital } = useHospitalApp();
		const timezoneName = currentHospital?.store?.timezoneName;
		const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<boolean>(false);
		const isAdmin = configuration.userHasRole('Admin');

		const currentRoute = useCurrentRoute();
		const isCommunicationTemplateRoute = currentRoute!.pathname.toLowerCase().includes('communicationtemplates');
		const currentMacroTargetType = isCommunicationTemplateRoute ? MacroTargetType.ExamVisit : MacroTargetType.Sms;
		let isReadOnly = false;
		const isEditing = macro?.macroId !== undefined && macro?.macroId !== 0;

		if (isEditing) {
			switch (macro.macroTypeId) {
				case MacroType.Hospitals:
					// Hospital templates are read only if the current user doesn't have permissions or if the current scope is not Hospitals (Hospital templates must be managed from Hospital Admin)
					isReadOnly =
						!configuration.HasUIFlag(
							AuthOperationUINames.UI_HospitalFlags_CanEditAllCommunicationTemplates
						) || macroScope != MacroType.Hospitals;
					break;
				case MacroType.Location:
					isReadOnly = !configuration.HasUIFlag(
						AuthOperationUINames.UI_HospitalFlags_CanEditLocationCommunicationTemplates
					);
					break;
				case MacroType.Provider:
					// Provider templates are read only if the current user doesn't have permissions and are not the user who created the template
					isReadOnly =
						!configuration.HasUIFlag(
							AuthOperationUINames.UI_HospitalFlags_CanEditProviderCommunicationTemplates
						) && macro.createdByUserId != configuration.userId;
					break;
			}
		}

		const keywords = useMemo(() => {
			return new MacroKeywords({
				"{{hospitalname}}": "001 - Sarasota, FL",
				"{{hospitalemail}}": "example@petco.com",
				"{{hospitalphone}}": "555-555-5555",
				"{{hospitalcity}}": "Sarasota",
				"{{hospitaladdress}}": "4244 Berkshire Dr, Sarasota, FL 34241",
				"{{hospitalurl}}": "www.petco.com",
				"{{clientfirstname}}": "John",
				"{{clientlastname}}": "Doe",
				"{{clientfullname}}": "John Doe",
				"{{petname}}": "Fluffy",
				"{{vetname}}": "Dr. Jane Doe",
				"{{todaysdate}}": DateTime.now().setZone(timezoneName).toLocaleString(DateTime.DATE_SHORT),
				"{{petgender}}": "Female",
				"{{hospitalsms}}": "555-555-5555"
			});
		}, [timezoneName]);

		const defaultValues = useMemo(() => {
			return {
				macroId: macro?.macroId === undefined ? 0 : macro.macroId,
				isActive: macro?.isActive === undefined ? true : macro.isActive,
				userId: macroScope == MacroType.Provider ? selectedUserId : undefined,
				macroTypeId: macroScope == MacroType.Provider ? MacroType.Provider : undefined,
				preview: isEditing ? MacroKeywords.processTextForMacros(macro.content, keywords) : undefined,
				...macro,
			};
		}, [isEditing, keywords, macro, macroScope, selectedUserId]);

		const methods = useForm<MacroModel | { preview: string }>({ defaultValues: spreadAndtransformDefaultValues(defaultValues) });
		const {
			control,
			handleSubmit,
			watch,
			reset,
			register,
			unregister,
			setValue,
			formState: { isDirty },
		} = methods;
		const unsavedChangesPrompt = useUnsavedChangesWarning(isDirty);

		const macroType = watch("macroTypeId");
		const content = watch("content");

		useEffect(() => {
			reset(spreadAndtransformDefaultValues(defaultValues));
		}, [defaultValues, reset]);

		useEffect(() => {
			fetchHospitals && fetchHospitals();
		}, [fetchHospitals]);

		useEffect(() => {
			const correctedText = content ? MacroKeywords.processTextForMacros(content, keywords) : undefined;
			if (correctedText !== undefined) {
				setValue("preview", correctedText);
			} else {
				setValue("preview", "");
			}
		}, [content, keywords, setValue]);

		// Admin, the user that created it, the user the template it is assigned to can delete template
		const disableDeleteButton = () =>
			macro?.macroTypeId === MacroType.Provider
				? !(macro.createdByUserId === configuration.userId ||
					macro.userId === configuration.userId ||
					isAdmin)
				: false;

		return (
			<>
				{unsavedChangesPrompt}
				<Row>
					<Col md={10}>
						<PageCard title={`Hospital Communication Template Details - ${currentMacroTargetType === MacroTargetType.ExamVisit ? 'Visit' : 'SMS'}`}>
							<YesNoConfirmModal
								visible={showDeleteConfirm}
								title="Delete Template"
								busy={false}
								description={
									"Are you sure you want to delete this Communication Template? It will no longer be available for use. "
								}
								onConfirm={() => onDelete(macro?.macroId!)}
								onCancel={() => setShowDeleteConfirm(false)}
							/>

							<FormProvider {...methods}>
								<Row className="pt-3 pb-3">
									<Col md={3}>
										<FormInput
											type="text"
											label="Name"
											fieldName="name"
											control={control}
											isRequired={true}
											highlightDirty={isEditing}
											disabled={isReadOnly}
										/>
									</Col>

									<Col
										md={2}
										style={{ maxWidth: "125px" }}
									>
										<FormSelect
											label="Type"
											fieldName="macroTypeId"
											control={control}
											isRequired={true}
											highlightDirty={isEditing}
											isDisabled={macroScope == MacroType.Provider || isReadOnly}
											options={macroTypes}
											getOptionLabel={opt => opt.label}
											getOptionValue={opt => opt.value.toString()}
											mapValueToOption={value => find(macroTypes, { value })}
											mapOptionToValue={opt => opt?.value}
											onOptionChanged={option => {
												switch (option?.value) {
													case MacroType.Location:
														unregister("userId");
														macroScope == MacroType.Location &&
															setValue("hospitalId", currentHospital?.hospitalId! ?? null);
														break;
													case MacroType.Provider:
														unregister("hospitalId");
														break;
													case MacroType.Hospitals:
														unregister("hospitalId");
														unregister("userId");
														break;
												}
											}}
										/>
									</Col>
									<Col md={3}>
										<FormSelect
											label="Assign to (Location)"
											fieldName="hospitalId"
											control={control}
											isRequired={macroType == MacroType.Location}
											isDisabled={macroType != MacroType.Location || isReadOnly}
											highlightDirty={isEditing}
											options={hospitals}
											getOptionLabel={s => s.name}
											getOptionValue={s => s.hospitalId.toString()}
											mapOptionToValue={opt => opt?.hospitalId}
											mapValueToOption={hospitalId =>
												find(hospitals, { hospitalId: hospitalId ?? 0 })
											}
										/>
									</Col>
									<Col md={3}>
										<FormSelect
											label="Assign to (User)"
											fieldName="userId"
											control={control}
											isRequired={macroType == MacroType.Provider}
											isDisabled={
												macroType != MacroType.Provider ||
												macroScope == MacroType.Provider ||
												isReadOnly
											}
											highlightDirty={isEditing}
											options={users}
											isClearable
											mapValueToOption={value => find(users, { value })}
											mapOptionToValue={opt => opt?.value}
										/>
									</Col>
									<Col md={1}>
										<Form.Label>Active?</Form.Label>
										<ToggleButton
											{...register(`isActive` as const)}
											noSlider={isReadOnly}
											defaultChecked={macro?.isActive}
										/>
									</Col>
								</Row>
								<Row className="pt-3">
									<Col md={isReadOnly ? 12 : 6}>
										<FormInput
											type="richtext"
											label="Content"
											fieldName="content"
											control={control}
											isRequired={true}
											highlightDirty={isEditing}
											disabled={isReadOnly}
											rows={8}
										/>
									</Col>
									{!isReadOnly && (
										<Col md={6}>
											<FormInput
												type="richtext"
												label="Preview"
												fieldName="preview"
												control={control}
												isRequired={true}
												disabled={true}
												rows={8}
												className="preview"
											/>
										</Col>
									)}
								</Row>
								<Row className="pt-3">
									{!isReadOnly && isEditing && (
										<Col>
											<Button
												variant="outline-danger"
												disabled={disableDeleteButton()}
												onClick={() => setShowDeleteConfirm(true)}
											>
												Delete
											</Button>
										</Col>
									)}
									{!isReadOnly && (
										<Col>
											<div className="d-flex flex-fill justify-content-end align-items-end">
												<SuspenseButton
													tags={[
														HospitalService.tags.createMacro,
														HospitalService.tags.updateMacro,
													]}
													variant="light"
													className={classNames("cancel", "text-uppercase")}
													onClick={onCancel}
												>
													Cancel
												</SuspenseButton>
												<SuspenseButton
													tags={[
														HospitalService.tags.createMacro,
														HospitalService.tags.updateMacro,
													]}
													onClick={handleSubmit(onSubmit)}
													className="text-uppercase"
												>
													{"Save Template"}
												</SuspenseButton>
											</div>
										</Col>
									)}
								</Row>
								<Row className="pt-3">
									<Col className="fw-normal-bold ps-4">
										<Row>
											{isEditing
												? `Last Modified By: ${macro.userUpdated}`
												: `Created By: ${configuration.IsDVM()
													? `Dr. ${configuration.firstName} ${configuration.lastName}`
													: `${configuration.firstName} ${configuration.lastName}`
												}`}
										</Row>
										<Row>
											{isEditing
												? `Date Last Modified: ${macro?.dateUpdated
													?.setZone(currentHospital?.store?.timezoneName)
													.toLocaleString(DateTime.DATE_FULL)}
                                at ${macro?.dateUpdated
													?.setZone(currentHospital?.store?.timezoneName)
													.toLocaleString(DateTime.TIME_SIMPLE)}`
												: `Date Created: ${DateTime.now()
													.setZone(currentHospital?.store?.timezoneName)
													.toLocaleString(DateTime.DATE_FULL)}
                                at ${DateTime.now()
													.setZone(currentHospital?.store?.timezoneName)
													.toLocaleString(DateTime.TIME_SIMPLE)}`}
										</Row>
									</Col>
								</Row>
							</FormProvider>
						</PageCard>
					</Col>

					<Col
						md={2}
						className="d-flex"
					>
						<div className="d-flex flex-grow-1">
							<PageCard
								title="Keywords"
								className="flex-grow-1"
							>
								<Table hover>
									<thead>
										<tr
											key={"header"}
											className=""
										>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{Object.keys(keywords).map((a: string) => (
											<tr key={a}>
												<td>{a}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</PageCard>
						</div>
					</Col>
				</Row>
			</>
		);
	}
);
